/**
 * PRICING — Source unique et centralisée de tous les tarifs.
 *
 * ⚠️ TOUT changement de tarif se fait ICI. Ne JAMAIS hardcoder un tarif ailleurs.
 * Importer dans les composants : import { PRICING, formatPrice } from '@/data/pricing';
 *
 * Structure : catégorie > prestation > { price, priceHT, unit, currency, includes, notes }
 */

export const PRICING = {
  // ═══════════════════════════════════════════════════════════════════════
  // ANNIVERSAIRES ENFANTS
  // ═══════════════════════════════════════════════════════════════════════
  anniversaire: {
    mascotte: {
      label: 'Formule Mascotte',
      price: 110,
      unit: '1 heure',
      currency: 'EUR',
      includes: ['1 mascotte au choix', 'animation photo', 'ambiance festive'],
    },
    clownMagicien: {
      label: 'Formule Clown Magicien',
      price: 220,
      unit: '1h30',
      currency: 'EUR',
      includes: ['spectacle de magie interactif', 'sculptures ballons', 'photos souvenir'],
    },
    magiqueMascotte: {
      label: 'Formule Magique + Mascotte',
      price: 270,
      unit: '1h30',
      currency: 'EUR',
      includes: ['magicien + 1 mascotte', 'sculptures ballons', 'ambiance festive'],
    },
    chateauGonflable: {
      label: 'Formule Château Gonflable',
      price: 290,
      unit: 'demi-journée',
      currency: 'EUR',
      includes: ['château gonflable', 'clown ou mascotte', 'installation incluse'],
    },
    feteForaine: {
      label: 'Formule Fête Foraine',
      price: 350,
      unit: 'demi-journée',
      currency: 'EUR',
      includes: ['3 machines (barbe à papa, pop-corn, granité)', 'animateur', 'consommables'],
      note: 'À partir de',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // MASCOTTES
  // ═══════════════════════════════════════════════════════════════════════
  mascotte: {
    solo: {
      label: 'Mascotte seule',
      price: 110,
      unit: '30 min à 1h',
      currency: 'EUR',
    },
    duo: {
      label: 'Duo de mascottes',
      price: 160,
      unit: '30 min à 1h',
      currency: 'EUR',
    },
    premium: {
      label: 'Mascotte Premium (spectacle personnalisé)',
      price: 210,
      unit: '1h',
      currency: 'EUR',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // SPECTACLES B2B / ÉCOLE / CSE
  // ═══════════════════════════════════════════════════════════════════════
  spectacles: {
    minimum: 850,
    ecoleMaternelle: {
      label: 'Spectacle École Maternelle',
      price: 850,
      priceHT: 708,
      unit: '30-45 min',
      currency: 'EUR',
      note: 'HT',
    },
    ecolePrimaire: {
      label: 'Spectacle École Primaire',
      price: 850,
      priceHT: 708,
      unit: '45 min',
      currency: 'EUR',
      note: 'HT',
    },
    packEcole: {
      label: 'Pack École (maternelle + primaire)',
      price: 850,
      priceHT: 708,
      unit: 'journée',
      currency: 'EUR',
      note: 'HT',
    },
    arbreNoelCse: {
      label: 'Spectacle Arbre de Noël CSE',
      price: 850,
      priceHT: 708,
      unit: '1h',
      currency: 'EUR',
      note: 'HT',
    },
    arbreNoelPremium: {
      label: 'Pack CSE Premium (magie + Père Noël + mascotte)',
      price: 1150,
      priceHT: 958,
      unit: '2h',
      currency: 'EUR',
      note: 'HT',
    },
    arbreNoelGrandFormat: {
      label: 'Pack CSE Grand Format (100-300 enfants)',
      price: 1500,
      priceHT: 1250,
      unit: '2h+',
      currency: 'EUR',
      note: 'HT+',
    },
    pereNoelSeul: {
      label: 'Père Noël seul',
      price: 300,
      priceHT: 250,
      unit: '30 min à 1h',
      currency: 'EUR',
      note: 'HT',
    },
    festivalMedieval: {
      label: 'Spectacle Festival Médiéval',
      price: 850,
      priceHT: 708,
      unit: '45 min',
      currency: 'EUR',
      note: 'HT — sur devis selon nombre de sessions',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // LOCATION MACHINES (particuliers, TTC)
  // ═══════════════════════════════════════════════════════════════════════
  locations: {
    barbeAPapa: {
      label: 'Location Machine Barbe à Papa',
      price: 60,
      priceWeekend: 90,
      pricePackFamille: 120,
      pricePackWeekendFamille: 150,
      unit: '1 jour',
      currency: 'EUR',
      note: 'TTC — sucre inclus, livraison 30 km Quimper',
      capacity: '150 barbes/heure',
      power: '220V — 1200W',
      dimensions: '55 × 55 × 45 cm',
    },
    popCorn: {
      label: 'Location Machine Pop-Corn',
      price: 90,
      priceWeekend: 130,
      pricePackFamille: 160,
      pricePackWeekendFamille: 200,
      unit: '1 jour',
      currency: 'EUR',
      note: 'TTC — grains + huile inclus, livraison 30 km Quimper',
      capacity: '4 kg/heure',
      power: '220V — 1300W',
      dimensions: '58 × 45 × 65 cm',
    },
    granite: {
      label: 'Location Machine Granité',
      price: 140,
      priceWeekend: 190,
      pricePackFamille: 190,
      pricePackWeekendFamille: 240,
      unit: '1 jour',
      currency: 'EUR',
      note: 'TTC — sirop 2 parfums inclus, livraison 30 km Quimper',
      capacity: '2 bacs × 7 litres',
      power: '220V — 500W par bac',
      dimensions: '45 × 46 × 78 cm',
    },
    glaceItalienne: {
      label: 'Location Machine Glace Italienne',
      price: 200,
      priceWeekend: 350,
      priceWeek: 700,
      priceMonth: 1200,
      pricePackMariage: 290,
      unit: '1 jour',
      currency: 'EUR',
      note: 'TTC (mois HT) — 3 parfums possibles, kit 58€ HT',
      capacity: '150 glaces/heure',
      power: '220V — 1800W',
      dimensions: '50 × 70 × 80 cm',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // LOUSTIC BREIZH — FOOD TRUCK
  // ═══════════════════════════════════════════════════════════════════════
  foodTruck: {
    hotDogSimple: { label: 'Hot Dog Le Simple', price: 7.00, currency: 'EUR' },
    hotDogCheddar: { label: 'Hot Dog Le Cheddar', price: 7.50, currency: 'EUR' },
    hotDogJalapenos: { label: 'Hot Dog Le Jalapeños', price: 7.50, currency: 'EUR' },
    hotDogComplet: { label: 'Hot Dog Le Complet', price: 8.00, currency: 'EUR' },
    nachos: { label: 'Nachos Cheddar', price: 4.00, currency: 'EUR' },
    glaceCornet: { label: 'Glace italienne cornet', price: 3.50, currency: 'EUR' },
    glacePetitPot: { label: 'Glace italienne petit pot', price: 3.50, currency: 'EUR' },
    glaceGrandPot: { label: 'Glace italienne grand pot', price: 4.50, currency: 'EUR' },
    gaufreSucre: { label: 'Gaufre sucre', price: 3.00, currency: 'EUR' },
    gaufreNutella: { label: 'Gaufre Nocciolata Bio', price: 3.50, currency: 'EUR' },
    gaufreCaramel: { label: 'Gaufre caramel beurre salé', price: 4.00, currency: 'EUR' },
    breizhCola: { label: 'Breizh Cola', price: 2.50, currency: 'EUR' },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // LIVRAISON / DÉPLACEMENT
  // ═══════════════════════════════════════════════════════════════════════
  delivery: {
    zoneGratuite: '30 km autour de Quimper',
    freeKm: 30,
    perKm: 0.7,
    currency: 'EUR',
  },
};

/**
 * Helper de formatage des prix
 */
const formatEuro = (v) => `${Number.isInteger(v) ? v : v.toFixed(2).replace('.', ',')} €`;
const deliveryCost = (km) => Math.round(Math.max(0, km - PRICING.delivery.freeKm) * PRICING.delivery.perKm * 100) / 100;
export const deliveryInfo = (km) => (km <= PRICING.delivery.freeKm ? 'gratuite (moins de 30 km de Quimper)' : `0,70 €/km au-delà de 30 km (soit ${formatEuro(deliveryCost(km))})`);
export const deliveryTag = (km) => (km <= PRICING.delivery.freeKm ? 'Gratuite' : formatEuro(deliveryCost(km)));

export const formatPrice = (item, opts = {}) => {
  if (!item) return '';
  const { showUnit = true, format = 'display' } = opts;
  const price = format === 'ht' && item.priceHT ? item.priceHT : item.price;
  const suffix = format === 'ht' ? ' HT' : '';
  if (typeof price !== 'number') return item.label || '';
  const unit = showUnit && item.unit ? ` / ${item.unit}` : '';
  return `${price}€${suffix}${unit}`;
};

/**
 * Range de prix (le minimum et le maximum du catalogue) — utile pour Schema.org priceRange
 */
export const PRICE_RANGE = { min: 60, max: 1500, formatted: '60-1500 EUR' };

/**
 * Fourchette catalogue affichable
 */
export const HEADLINE_PRICES = {
  anniversaire: 110,
  spectacle: 850,
  mascotte: 110,
  locationBarbeAPapa: 60,
  locationPopCorn: 90,
  locationGranite: 140,
  locationGlaceItalienne: 200,
  arbreNoelCse: 850,
  hotDog: 7,
};
